/**
 * Service Layer Auth Debug Utilities
 * 
 * E2E test hooks and debug utilities (only in dev/test builds).
 */

export interface AuthDebugState {
  email: string | null;
  ready: boolean;
  loading: boolean;
  csrfToken: string | null;
  authError: string | null;
  lastErrorCode: string | null;
  setEmail: (email: string | null) => void;
  setRole: (role: string | null) => void;
  setCsrfToken: (token: string | null) => void;
  setAuthError: (error: string | null) => void;
  setLastErrorCode: (code: string | null) => void;
}

/**
 * Setup E2E test hooks (only in dev/test builds)
 */
export function setupAuthDebug(state: AuthDebugState, testMode: boolean) {
  if (typeof window === 'undefined') return () => {};
  
  // Only expose in dev/test mode
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development' || testMode;
  if (!isDev) return () => {};
  
  // Expose minimal testing API for CI and Storybook
  (window as any).AUTH_DEBUG = {
    forceUnauthed: () => {
      state.setEmail(null);
      state.setRole(null);
      state.setCsrfToken(null);
      state.setAuthError(null);
      state.setLastErrorCode(null);
      console.warn('[AUTH_DEBUG] Forced unauthenticated state');
    },
    forceAuthed: (testEmail: string) => {
      state.setEmail(testEmail || 'test@clanda.de');
      state.setAuthError(null);
      state.setLastErrorCode(null);
      console.warn(`[AUTH_DEBUG] Forced authenticated state: ${testEmail || 'test@clanda.de'}`);
    },
    getState: () => ({
      email: state.email,
      isAuthenticated: state.email !== null,
      ready: state.ready,
      loading: state.loading,
      csrfToken: state.csrfToken,
      authError: state.authError,
      lastErrorCode: state.lastErrorCode,
    }),
  };
  
  // Cleanup function
  return () => {
    if ((window as any).AUTH_DEBUG) {
      delete (window as any).AUTH_DEBUG;
    }
  };
}

